import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { isIneligibleAtClub } from '@/pages/benefits/clubBenefitEligibility'
import { useCanUseBenefits } from '@/hooks/useCanUseBenefits'
import { useClaimableBenefits } from '@/services/myBenefits'

import { ClubBenefitsModal } from './ClubBenefitsModal'

type Props = {
  benefitTypes?: string[]
  clubId: number
  clubName: string
}

/**
 * A mark beside a club's name saying it is in the DUPR+ Benefits program, and the way into what
 * that means here.
 *
 * Sized and worded for scanning, not reading. On a browse list the useful signal is the binary —
 * which of these clubs are in the program at all — and that is what the eye picks out going down
 * the column. How many programs a club runs barely moves a browse decision while being most of
 * the label's length, so the count is spoken to screen readers and which ones is a click away.
 *
 * One badge and not two. Saying "Accepts 1 DUPR+ Benefit" beside "Not eligible" states a fact about
 * the club next to a fact about the member and reads as a contradiction, because the first is
 * naturally heard as "accepts a benefit YOU have". Eligibility is a shade OF that claim, not a
 * second claim, so it is carried by the badge's tone.
 *
 * The wording stays the same in both tones because the two directions are not equally knowable —
 * see {@link isIneligibleAtClub}. Only the negative is certain, so dimming is the strongest thing
 * that can honestly be said, and the blue tone promises participation, not that a claim will work.
 *
 * Mirrors `ClubBenefitTag.tsx` in the mobile app.
 */
export const ClubBenefitBadge = ({ benefitTypes, clubId, clubName }: Props) => {
  const { t } = useTranslation()
  const benefitsEnabled = useCanUseBenefits()
  const { data: claimable } = useClaimableBenefits(benefitsEnabled)
  const [open, setOpen] = useState(false)

  const offered = benefitTypes ?? []
  // A club in no program gets no badge at all. Most clubs are in none, and a mark on every row is
  // one the eye stops sorting by — which is the only thing this is for.
  //
  // The feature gate sits here rather than at the call site: `benefitTypes` rides the club search
  // document, so every club row would otherwise keep its badge for users the feature is not on for,
  // whatever the listing page remembered to check.
  if (!benefitsEnabled || offered.length === 0) return null

  const ineligible = isIneligibleAtClub(offered, claimable ?? [])
  const label = t('benefits.clubAcceptsBenefits', { count: offered.length })

  return (
    <>
      <button
        type="button"
        // The row navigates to the club; this must not, so the click stops here.
        onClick={(event) => {
          event.stopPropagation()
          setOpen(true)
        }}
        aria-label={ineligible ? `${label}. ${t('benefits.clubNotEligible')}` : label}
        className={`mt-1 inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium transition-colors ${
          ineligible
            ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            : 'bg-[#3A79E0]/10 text-[#2D5FB8] hover:bg-[#3A79E0]/20'
        }`}
      >
        <GiftIcon />
        {t('benefits.clubBenefitBadge')}
      </button>
      <ClubBenefitsModal open={open} onClose={() => setOpen(false)} clubId={clubId} clubName={clubName} />
    </>
  )
}

/** Inline rather than an icon-set import, so the badge carries no dependency of its own. */
const GiftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-3 w-3" aria-hidden="true">
    <path d="M20 12v9H4v-9M2 7h20v5H2zM12 21V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
  </svg>
)
